import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import GoogleAuthButton from "@/components/GoogleAuthButton";

function fmtErr(detail) {
  if (!detail) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export function Login() {
  const { login } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenue !");
      navigate(params.get("next") || "/");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24 fade-in">
      <h1 className="font-serif text-4xl tracking-tight">Se connecter</h1>
      <p className="mt-2 text-muted-foreground text-sm">Bon retour parmi nous.</p>

      <div className="mt-8">
        <GoogleAuthButton testId="google-login-btn" />
      </div>
      <div className="my-6 flex items-center gap-4">
        <div className="h-px bg-border flex-1" />
        <span className="text-xs text-muted-foreground uppercase tracking-widest">ou</span>
        <div className="h-px bg-border flex-1" />
      </div>

      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label>Email</Label>
          <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 rounded-sm bg-transparent" />
        </div>
        <div>
          <Label>Mot de passe</Label>
          <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 rounded-sm bg-transparent" />
        </div>
        <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full rounded-sm h-11">
          {loading ? "Connexion..." : "Se connecter"}
        </Button>
      </form>
      <div className="mt-8 text-sm text-muted-foreground text-center">
        Pas encore de compte ? <Link to="/register" className="text-accent hover:underline">Créer un compte</Link>
      </div>
    </div>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Compte créé !");
      navigate("/");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24 fade-in">
      <h1 className="font-serif text-4xl tracking-tight">Créer un compte</h1>
      <p className="mt-2 text-muted-foreground text-sm">Rejoignez la communauté des lecteurs.</p>

      <div className="mt-8">
        <GoogleAuthButton testId="google-register-btn" label="S'inscrire avec Google" />
      </div>
      <div className="my-6 flex items-center gap-4">
        <div className="h-px bg-border flex-1" />
        <span className="text-xs text-muted-foreground uppercase tracking-widest">ou</span>
        <div className="h-px bg-border flex-1" />
      </div>

      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label>Nom</Label>
          <Input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-2 rounded-sm bg-transparent" />
        </div>
        <div>
          <Label>Email</Label>
          <Input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 rounded-sm bg-transparent" />
        </div>
        <div>
          <Label>Mot de passe</Label>
          <Input data-testid="register-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 rounded-sm bg-transparent" />
        </div>
        <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full rounded-sm h-11">
          {loading ? "Création..." : "Créer mon compte"}
        </Button>
      </form>
      <div className="mt-8 text-sm text-muted-foreground text-center">
        Déjà un compte ? <Link to="/login" className="text-accent hover:underline">Se connecter</Link>
      </div>
    </div>
  );
}
